import {
    EbPlansNycEventCardChipExperiment,
    EbPlansNycEventCardChipValues,
    EventCardHomepageImageExperiment,
    EventCardImageExperimentValues,
    OrganizerAutocompleteExperiment,
    SearchEventCardLikesCountExperiment,
    SearchEventCardLikesCountExperimentValues,
    SearchEventCardLocalityVenueExperiment,
    SearchOpenDiscountsExperiment,
} from '@eventbrite/discover-utils';
import { FormattedEvent, hasCarouselWidget } from '@eventbrite/event-renderer';
import { useMapExperiment as BaseUseMapExperiment } from '@eventbrite/search-utils';
import { isStatsigInitialized } from '@eventbrite/statsig';
import {
    Statsig,
    useExperimentWithExposureLoggingDisabled,
} from 'statsig-react';
import { useEnvContext } from '../../context';
import {
    useClientStatsigEnrollment,
    useServerEnrolledStatsigExperiment,
} from '../../context/experiments';

export const useMapExperiment = () => {
    const envContext = useEnvContext();
    const isMobile = envContext?.env?.isMobile;
    return BaseUseMapExperiment(!!isMobile);
};

export const useMultiImageExperiment = (event: FormattedEvent) => {
    const { variant: eventCardImageExperiment } = useClientStatsigEnrollment({
        name: EventCardHomepageImageExperiment.name,
        paramName: EventCardHomepageImageExperiment.paramName,
        defaultValue: EventCardHomepageImageExperiment.values.control,
        enabled: event.isPromoted && hasCarouselWidget(event),
    });
    return eventCardImageExperiment === EventCardImageExperimentValues.multi;
};

export const useShowLikesExperiment = (isPromoted?: boolean) => {
    const { variant: showLikesEventCardExperiment } =
        useClientStatsigEnrollment({
            name: SearchEventCardLikesCountExperiment.name,
            paramName: SearchEventCardLikesCountExperiment.paramName,
            defaultValue: SearchEventCardLikesCountExperiment.values.control,
            enabled: isPromoted,
        });

    return (
        showLikesEventCardExperiment ===
        SearchEventCardLikesCountExperimentValues.showLikes
    );
};

export const useShowLocalityVenueExperiment = () => {
    const { variant: showLocalityVenueExperiment } =
        useServerEnrolledStatsigExperiment({
            name: SearchEventCardLocalityVenueExperiment.name,
            paramName: SearchEventCardLocalityVenueExperiment.paramName,
            defaultValue: SearchEventCardLocalityVenueExperiment.values.control,
            enabled: true,
        });

    return (
        showLocalityVenueExperiment ===
        SearchEventCardLocalityVenueExperiment.values.test
    );
};

export const useOpenDiscountsExperiment = (
    hasOpenDiscounts?: boolean,
    isPromoted?: boolean,
) => {
    const { variant } = useServerEnrolledStatsigExperiment({
        name: SearchOpenDiscountsExperiment.name,
        paramName: SearchOpenDiscountsExperiment.paramName,
        defaultValue: SearchOpenDiscountsExperiment.values.control,
        enabled: hasOpenDiscounts && !isPromoted,
    });

    return variant === SearchOpenDiscountsExperiment.values.test;
};

export const useOrganizerAutocompleteExperiment = () => {
    const { config, isLoading } = useExperimentWithExposureLoggingDisabled(
        OrganizerAutocompleteExperiment.name,
    );

    return {
        isLoading,
        value: config.get(
            OrganizerAutocompleteExperiment.paramName,
            OrganizerAutocompleteExperiment.values.control,
        ),
    };
};

export const manuallyLogAutocompleteExperiment = () => {
    if (isStatsigInitialized()) {
        Statsig.manuallyLogExperimentExposure(
            OrganizerAutocompleteExperiment.name,
            false,
        );
    }
};

const NYC_CITIES = new Set([
    'new york',
    'brooklyn',
    'queens',
    'bronx',
    'staten island',
    'manhattan',
]);

const isNycEvent = (venue?: FormattedEvent['venue']): boolean => {
    const city = venue?.address?.city?.toLowerCase();
    const region = venue?.address?.region;
    if (!city || !region) return false;
    return region === 'NY' && NYC_CITIES.has(city);
};

export const useEbPlansNycChipExperiment = (
    isPromoted?: boolean,
    venue?: FormattedEvent['venue'],
    isOnlineEvent?: boolean,
) => {
    const isNyc = isNycEvent(venue);

    const { variant } = useClientStatsigEnrollment({
        name: EbPlansNycEventCardChipExperiment.name,
        paramName: EbPlansNycEventCardChipExperiment.paramName,
        defaultValue: EbPlansNycEventCardChipExperiment.values.control,
        enabled: isPromoted && isNyc && !isOnlineEvent,
    });

    return variant === EbPlansNycEventCardChipValues.showChip;
};
